import {
  Button,
  Card,
  Checkbox,
  Flex,
  Form,
  Input,
  InputNumber,
  Select,
  theme,
  Typography,
} from 'antd';
import { type IResult } from 'jsonapi-react';
import { type ReactNode, useEffect, useMemo } from 'react';

import { catchError, EMPTY, from, of, switchMap, tap, throwError } from 'rxjs';
import { Beverage } from '../../../entities';
import MealCategory from '../../../entities/meal-category';
import Supply from '../../../entities/supply';
import { useNotification } from '../../../hooks/use-notification';
import { useMutation } from '../../../jsonapi-provider';
import { mealTypes, weekDays } from '../../../types';
import utils from '../../../utils/utils';

const SUCCESS_UPDATE_MESSAGE = 'Meal category updated successfully!';
const SUCCESS_ADD_MESSAGE = 'Meal category added successfully!';

interface DataType {
  mealCategory: MealCategory;
  onSuccess: (mealCategory: MealCategory) => void;
  onCancel: () => void;
  supplies: IResult<Supply[]>;
  beverages: IResult<Beverage[]>;
}

const MealCategoryForm = (props: DataType): ReactNode => {
  const notification = useNotification();
  const { mealCategory, onSuccess, onCancel, supplies, beverages } = props;
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const isEditMode = useMemo(() => !!mealCategory?.id, [mealCategory?.id]);
  const [mutateMealCategory, mealCategories] = useMutation<MealCategory>(
    isEditMode ? ['meal-category', mealCategory?.id] : ['meal-category']
  );
  const isCompleteMeal = Form.useWatch('isCompleteMeal', form);
  const initialValues = useMemo(
    () => ({
      ...mealCategory,
      diets:
        mealCategory?.diets && mealCategory?.diets !== ''
          ? mealCategory?.diets?.split(',').length === mealTypes.allDiets.split(',').length
            ? ['all']
            : mealCategory?.diets?.split(',')
          : undefined,
      preferences:
        mealCategory?.preferences && mealCategory?.preferences !== ''
          ? mealCategory?.preferences?.split(',').length ===
            mealTypes.allPreferences.split(',').length
            ? ['all']
            : mealCategory?.preferences?.split(',')
          : undefined,
      supplies:
        mealCategory?.supplies.length === supplies.data?.length
          ? ['all']
          : mealCategory?.supplies
          ? mealCategory?.supplies?.map((sup) => sup.id) ?? []
          : undefined,
      beverages:
        mealCategory?.beverages.length === beverages.data?.length
          ? ['all']
          : mealCategory?.beverages
          ? mealCategory?.beverages?.map((bev) => bev.id) ?? []
          : undefined,
      availableWeekDays: utils.mapStringToWeekDays(mealCategory?.availableWeekDays ?? ''),
      defaultServeType: mealCategory?.id ? mealCategory.defaultServeType : undefined,
    }),
    [mealCategory]
  );

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [initialValues]);

  const onSubmit = (values: any): void => {
    from(
      mutateMealCategory(
        Object.assign(new MealCategory(), {
          ...values,
          sortOrder: undefined,
          diets: values.diets?.includes('all') ? mealTypes.allDiets : values.diets?.join(','),
          preferences: values.preferences?.includes('all')
            ? mealTypes.allPreferences
            : values.preferences?.join(','),
          supplies: (values.supplies.includes('all')
            ? (supplies.data ?? []).map((s) => s.id)
            : values.supplies
          ).map((supplyId: string) => {
            const supply = new Supply();
            supply.id = supplyId;
            return supply;
          }),
          beverages: (values.beverages.includes('all')
            ? (beverages.data ?? []).map((s) => s.id)
            : values.beverages
          ).map((bevId: string) => {
            const beverage = new Beverage();
            beverage.id = bevId;
            return beverage;
          }),
          availableWeekDays: utils.mapWeekDaysToString(values.availableWeekDays),
        })
      )
    )
      .pipe(
        switchMap((res) =>
          res.error
            ? throwError(() => new Error(res.error?.title ?? res.error?.detail ?? 'Unknown error'))
            : of(res.data!)
        ),
        tap((mc) => {
          notification.success({
            description: isEditMode ? SUCCESS_UPDATE_MESSAGE : SUCCESS_ADD_MESSAGE,
            message: '',
          });
          onSuccess(mc);
        }),
        catchError((error: Error) => {
          notification.error({
            description: error.message,
            message: ' ',
          });
          return EMPTY;
        })
      )
      .subscribe();
  };
  const handleCancel = (): void => {
    form.resetFields();
    onCancel();
  };

  return (
    <Flex vertical>
      <Card styles={{ body: { paddingBlock: 0 } }}>
        <Form
          form={form}
          id="meal-category-form"
          initialValues={initialValues}
          onFinish={onSubmit}
          layout="vertical"
          colon={false}
          requiredMark={false}
        >
          <Form.Item>
            <Typography.Title level={4}>
              {isEditMode ? mealCategory.name : 'New Meal Category'}
            </Typography.Title>
          </Form.Item>

          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>

          <Form.Item name="name" label="Name">
            <Input />
          </Form.Item>
          <Form.Item name="abbreviation" label="Abbreviation">
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input />
          </Form.Item>
          <Form.Item name="diets" label="Diets">
            <Select
              mode="multiple"
              allowClear
              style={{ minWidth: 300, maxWidth: 900 }}
              placeholder="Please select diets"
              options={[
                { label: 'All', value: 'all' },
                ...mealTypes.allDiets
                  .split(',')
                  .map((diet: string) => ({ label: diet, value: diet })),
              ]}
              onChange={(values) => {
                if (values?.length && values[values.length - 1] === 'all') {
                  form.setFieldValue('diets', 'all');
                } else {
                  if (values?.length && values[0] === 'all') {
                    const filteredSelected = values.filter((v: string) => v !== 'all');
                    form.setFieldValue('diets', filteredSelected);
                  } else {
                    form.setFieldValue('diets', values);
                  }
                }
              }}
            />
          </Form.Item>
          <Form.Item name="preferences" label="Preferences">
            <Select
              mode="multiple"
              allowClear
              style={{ minWidth: 300, maxWidth: 900 }}
              placeholder="Please select preferences"
              options={[
                { label: 'All', value: 'all' },
                ...mealTypes.allPreferences
                  .split(',')
                  .map((prefrence: string) => ({ label: prefrence, value: prefrence })),
              ]}
              onChange={(values) => {
                if (values?.length && values[values.length - 1] === 'all') {
                  form.setFieldValue('preferences', 'all');
                } else {
                  if (values?.length && values[0] === 'all') {
                    const filteredSelected = values.filter((v: string) => v !== 'all');
                    form.setFieldValue('preferences', filteredSelected);
                  } else {
                    form.setFieldValue('preferences', values);
                  }
                }
              }}
            />
          </Form.Item>
          <Form.Item name="beverages" label="Beverages">
            <Select
              mode="multiple"
              options={[
                { label: 'All', value: 'all' },
                ...(beverages.data ?? []).map((b) => ({ label: b.name, value: b.id })),
              ]}
              placeholder="Please select beverages"
              onChange={(values) => {
                if (values?.length && values[values.length - 1] === 'all') {
                  form.setFieldValue('beverages', 'all');
                } else {
                  if (values?.length && values[0] === 'all') {
                    const filteredSelected = values.filter((v: string) => v !== 'all');
                    form.setFieldValue('beverages', filteredSelected);
                  } else {
                    form.setFieldValue('beverages', values);
                  }
                }
              }}
            />
          </Form.Item>
          <Form.Item name="supplies" label="Supplies">
            <Select
              mode="multiple"
              options={[
                { label: 'All', value: 'all' },
                ...(supplies.data ?? []).map((s) => ({ label: s.name, value: s.id })),
              ]}
              placeholder="Please select supplies"
              onChange={(values) => {
                if (values?.length && values[values.length - 1] === 'all') {
                  form.setFieldValue('supplies', 'all');
                } else {
                  if (values?.length && values[0] === 'all') {
                    const filteredSelected = values.filter((v: string) => v !== 'all');
                    form.setFieldValue('supplies', filteredSelected);
                  } else {
                    form.setFieldValue('supplies', values);
                  }
                }
              }}
            />
          </Form.Item>
          <Form.Item name="availableWeekDays" label="Available week days">
            <Select
              mode="multiple"
              allowClear
              style={{ minWidth: 300, maxWidth: 900 }}
              placeholder="Please select week days"
              options={weekDays.map((day) => ({ label: day, value: day }))}
            />
          </Form.Item>
          <Form.Item
            name="defaultServeType"
            label="Default serve type"
            style={{ marginBlockEnd: 20 }}
            rules={[{ required: true }]}
          >
            <Select
              options={Object.entries(utils.serveTypesLabels)}
              placeholder="Select default serve type"
              fieldNames={{ value: '0', label: '1' }}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            name="minQuantityAllowed"
            style={{ marginBlockEnd: token.marginSM }}
            label="Min Allowed Quantity"
          >
            <InputNumber addonAfter="Meal" min={1} precision={0} />
          </Form.Item>
          <Form.Item name="beverageFactor" style={{ marginBlockEnd: 0 }} label="Beverage Factor">
            <InputNumber addonAfter="Beverage" min={1} precision={0} />
          </Form.Item>
          <Flex
            style={{
              marginTop: token.marginLG,
            }}
            align="start"
            gap={30}
          >
            <Form.Item name="isCompleteMeal" valuePropName="checked">
              <Checkbox>Is complete meal?</Checkbox>
            </Form.Item>
            <Form.Item name="units" style={{ marginBlockEnd: 0 }}>
              <InputNumber
                disabled={isCompleteMeal === false}
                addonAfter="Units per meal"
                min={1}
                precision={0}
              />
            </Form.Item>
          </Flex>
          <Form.Item name="hasDiabeticType" valuePropName="checked">
            <Checkbox>Has a diabetic type</Checkbox>
          </Form.Item>
          <Form.Item
            name="isGeneric"
            valuePropName="checked"
            style={{ marginBlockEnd: 0, marginTop: 20 }}
          >
            <Checkbox>Generic meal category</Checkbox>
          </Form.Item>
          <Form.Item
            name="isTestable"
            valuePropName="checked"
            style={{ marginBlockEnd: 0, marginTop: 20 }}
          >
            <Checkbox>Is testable meal category</Checkbox>
          </Form.Item>
          <Form.Item
            name="isPrepackable"
            valuePropName="checked"
            style={{ marginBlockEnd: 0, marginTop: 20 }}
          >
            <Checkbox>Can be prebacked?</Checkbox>
          </Form.Item>
          <Form.Item
            name="allowC1OrC2"
            valuePropName="checked"
            style={{ marginBlockEnd: 0, marginBlock: 20 }}
          >
            <Checkbox>Allow selecting C1 or C2</Checkbox>
          </Form.Item>
          <Form.Item
            name="defaultServiceType"
            label="Default service type"
            style={{ marginBlockEnd: 20 }}
            rules={[{ required: true }]}
          >
            <Select
              defaultActiveFirstOption
              options={Object.entries(utils.serviceTypesLabels)}
              fieldNames={{ value: '0', label: '1' }}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item>
            <Flex gap="small" style={{ flexDirection: 'row-reverse' }}>
              <Button block htmlType="submit" type="primary">
                Save
              </Button>
              <Button block onClick={onCancel} disabled={mealCategories.isLoading}>
                Discard
              </Button>
            </Flex>
          </Form.Item>
        </Form>
      </Card>
    </Flex>
  );
};

MealCategoryForm.defaultProps = {
  onConfirm: () => {},
  onCancel: () => {},
};

MealCategoryForm.displayName = 'EditMealCategorySection';
export default MealCategoryForm;
